/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { DraggableBlockTypes } from "constants/DraggableBlockTypes";
import { useCallback, useState } from "react";
import { ConnectDropTarget, useDrop } from "react-dnd";
import { WaveformBlock, PlaceholderBlock } from "types/Block";
import { useCleanedUpTimeout } from "hooks/useCleanedUpTimeout";
import { useStudioAcceptedContentTypes } from "./useStudioAcceptedContentTypes";

export interface SingleFileDropHandler {
  (droppedFile: File): void;
  type: "SingleFileDropHandler";
}

export const makeSingleFileDropHandler = (
  handler: (droppedFile: File) => void
): SingleFileDropHandler => {
  return Object.assign(handler, { type: "SingleFileDropHandler" } as {
    type: "SingleFileDropHandler";
  });
};

export interface MultiFileDropHandler {
  (droppedFiles: File[]): void;
  type: "MultiFileDropHandler";
}

export const makeMultiFileDropHandler = (
  handler: (droppedFiles: File[]) => void
): MultiFileDropHandler => {
  return Object.assign(handler, { type: "MultiFileDropHandler" } as {
    type: "MultiFileDropHandler";
  });
};

interface UseDropWaveformBlockOrNativeFileOptions {
  onBlockDroppedHandler?: (
    droppedBlock: WaveformBlock | PlaceholderBlock
  ) => void;
  onFileDroppedHandler?: SingleFileDropHandler | MultiFileDropHandler;
  acceptedContentTypes?: string[];
}

interface UseDropWaveformBlockOrNativeFileResult {
  isDraggingOver: boolean;
  dropRef: ConnectDropTarget;
  filePickerHandler: (files: FileList) => void; // helper to ensure consistenency when allowing drag + drop or using the file browser
}

const DEFAULT_ACCEPTABLE_DRAGGABLE_TYPES = [
  DraggableBlockTypes.BLOCK,
  DraggableBlockTypes.NATIVE_FILE,
];

export default function useDropWaveformBlockOrNativeFile({
  onBlockDroppedHandler,
  onFileDroppedHandler,
  acceptedContentTypes,
}: UseDropWaveformBlockOrNativeFileOptions): UseDropWaveformBlockOrNativeFileResult {
  const defaultContentTypes = useStudioAcceptedContentTypes();
  if (!acceptedContentTypes) {
    acceptedContentTypes = defaultContentTypes;
  }
  const [wasAbleToDrop, setWasAbleToDrop] = useState<boolean>(false);

  const setCleanedUpTimeout = useCleanedUpTimeout();

  const onFileList = useCallback(
    (droppedFiles: File[]) => {
      if (!onFileDroppedHandler) {
        return;
      }
      switch (onFileDroppedHandler.type) {
        case "SingleFileDropHandler":
          onFileDroppedHandler(droppedFiles[0]);
          break;
        case "MultiFileDropHandler":
          onFileDroppedHandler(droppedFiles);
          break;
      }
    },
    [onFileDroppedHandler]
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DEFAULT_ACCEPTABLE_DRAGGABLE_TYPES,
      canDrop: (item: DataTransfer, monitor): boolean => {
        const itemType = monitor.getItemType();
        switch (itemType) {
          case DraggableBlockTypes.NATIVE_FILE:
            if (!onFileDroppedHandler) {
              setCleanedUpTimeout(() => {
                setWasAbleToDrop(() => false);
              }, 0);
              return false;
            }

            const mimeTypes = getMimeTypes(item);

            // Don't allow multi-file drag for single file handler
            if (
              onFileDroppedHandler.type == "SingleFileDropHandler" &&
              mimeTypes.length > 1
            ) {
              setCleanedUpTimeout(() => {
                setWasAbleToDrop(() => false);
              }, 0);
              return false;
            }

            // Seems like the mime type of a native file we get from react-dnd is intermittently present,
            // critically it flickers when the drag moves over a new drop target. This makes the `canDrop`
            // method return false incorrectly, which prevents the drop targets from being highlighted. The
            // fix is to save the last known mime type and use that for the canDrop checks.
            if (mimeTypes.length == 0) {
              return wasAbleToDrop;
            }

            // Reject drop unless all files are of an accepted type
            const allFilesAreAcceptable =
              mimeTypes.filter((type) => !acceptedContentTypes.includes(type))
                .length == 0;

            // without a timeout the library thinks we're calling monitor.canDrop inside this function
            setCleanedUpTimeout(() => {
              setWasAbleToDrop(() => allFilesAreAcceptable);
            }, 0);

            return allFilesAreAcceptable;
          case DraggableBlockTypes.BLOCK:
            return !!onBlockDroppedHandler;
        }
        return wasAbleToDrop;
      },
      drop: (item: DataTransfer | { block: WaveformBlock }, monitor): void => {
        const itemType = monitor.getItemType();
        switch (itemType) {
          case DraggableBlockTypes.NATIVE_FILE:
            if (!onFileDroppedHandler) {
              return;
            }

            const dataTransfer = item as DataTransfer;
            const items = dataTransfer?.items ?? [];
            const mimeTypes = getMimeTypes(dataTransfer);

            const allFilesAreAcceptable =
              mimeTypes.length > 0 &&
              mimeTypes.filter((type) => !acceptedContentTypes.includes(type))
                .length == 0;

            // Reject drop unless all files are of an accepted type
            if (!allFilesAreAcceptable) {
              // TODO: Toast? Prior implementation failed silently.
              return;
            }

            const droppedFiles: File[] = [];

            Array.from(items).forEach((item) => {
              const file = item.getAsFile();

              // Reject if any invalid files
              if (!file) {
                // TODO: Toast? Prior implementation failed silently.
                console.assert(
                  false,
                  "Dropping file failed. Could not process DataTransferItem as file: ",
                  item
                );

                return;
              }

              droppedFiles.push(file);
            });

            onFileList(droppedFiles);

            return;
          case DraggableBlockTypes.BLOCK:
            if (!onBlockDroppedHandler) {
              return;
            }

            const blockItem = item as {
              block: WaveformBlock | PlaceholderBlock;
            };
            const droppedBlock = blockItem?.block;
            if (!!droppedBlock) {
              onBlockDroppedHandler?.(droppedBlock);
            } else {
              console.assert(
                false,
                "Dropping file failed. Could not process dropped item as block: ",
                item
              );
            }
            return;
          default:
            console.assert(
              false,
              `Drop received invalid item type ${String(itemType)}`
            );
        }
      },
      collect: (monitor): { isOver: boolean; canDrop: boolean } => {
        return {
          isOver: !!monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
    }),
    [onBlockDroppedHandler, onFileDroppedHandler, wasAbleToDrop]
  );

  const filePickerHandler = useCallback(
    (files: FileList) => {
      const fileArray: File[] = [...files];
      onFileList(fileArray);
    },
    [onFileList]
  );

  return {
    // TODO: Expose canDrop separately if we want to show negative feedback to user (e.g. currently dragged file is wrong mime type)
    isDraggingOver: isOver && canDrop,
    dropRef: drop,
    filePickerHandler,
  };
}

export const getMimeType = (item: DataTransfer | null): string | undefined => {
  return getMimeTypes(item)[0];
};

export const getMimeTypes = (item: DataTransfer | null): string[] => {
  if (!!item) {
    if (item.files?.length) {
      return Array.from(item.files).map((file) => file.type);
    } else if (item.items?.length) {
      return Array.from(item.items).map((item) => item.type);
    }
  }

  return [];
};
